import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { client } from '..';
import { GET_ALL_HANDBOOKS, UPSERT_HANDBOOK, GET_HANDBOOKS_FOR_EMPLOYEE, DELETE_HANDBOOK } from "../Queries/handBook";

const initialState = {
  list: [],
  myHandbooks: [], // handbooks for currently logged-in employee
  myLoading: false,
  loading: false,
  saving: false,
  error: null,
};

const handbookSlice = createSlice({
  name: "handbook",
  initialState,
  reducers: {
    clearHandbookError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder

      // ================================
      // FETCH ALL HANDBOOKS
      // ================================
      .addCase(fetchAllHandbooks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllHandbooks.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload || [];
      })
      .addCase(fetchAllHandbooks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ================================
      // UPSERT HANDBOOK (CREATE / UPDATE)
      // ================================
      .addCase(upsertHandbook.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(upsertHandbook.fulfilled, (state, action) => {
        state.saving = false;

        const updated = action.payload;
        const index = state.list.findIndex(
          (h) => h._id === updated._id
        );

        if (index !== -1) {
          // UPDATE existing handbook
          state.list[index] = updated;
        } else {
          // CREATE new handbook
          state.list.unshift(updated);
        }
      })
      .addCase(upsertHandbook.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      // ================================
      // DELETE HANDBOOK
      // ================================
      .addCase(deleteHandbook.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(deleteHandbook.fulfilled, (state, action) => {
        state.saving = false;
        const removed = action.payload || {};
        const id = removed._id || removed.id;
        if (id) {
          state.list = state.list.filter((h) => h._id !== id && h.id !== id);
        }
      })
      .addCase(deleteHandbook.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      });

    // ================================
    // FETCH HANDBOOKS FOR CURRENT EMPLOYEE
    // ================================
    builder
      .addCase(fetchHandbooksForEmployee.pending, (state) => {
        state.myLoading = true;
        state.error = null;
      })
      .addCase(fetchHandbooksForEmployee.fulfilled, (state, action) => {
        state.myLoading = false;
        state.myHandbooks = action.payload || [];
      })
      .addCase(fetchHandbooksForEmployee.rejected, (state, action) => {
        state.myLoading = false;
        state.error = action.payload;
      });
  },
});

export const fetchAllHandbooks = createAsyncThunk(
  "handbook/fetchAllHandbooks",
  async ({ includeInactive = false } = {}, { rejectWithValue }) => {
    try {
      const { data } = await client.query({
        query: GET_ALL_HANDBOOKS,
        variables: { includeInactive },
        fetchPolicy: "network-only",
      });

      const handbooks = data?.getAllHandbooks;

      if (!handbooks) {
        return rejectWithValue("No handbooks found");
      }
      return handbooks;
    } catch (err) {
      return rejectWithValue(
        err?.message ||
        err?.graphQLErrors?.[0]?.message ||
        "Failed to fetch handbooks"
      );
    }
  }
);

export const deleteHandbook = createAsyncThunk(
  "handbook/deleteHandbook",
  async (
    { id, changedBy },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await client.mutate({
        mutation: DELETE_HANDBOOK,
        variables: { id, changedBy },
        fetchPolicy: "no-cache",
      });

      if (!data?.deleteHandbook) {
        return rejectWithValue("Failed to delete handbook");
      }

      return data.deleteHandbook;
    } catch (err) {
      return rejectWithValue(
        err?.message ||
          err?.graphQLErrors?.[0]?.message ||
          "Failed to delete handbook"
      );
    }
  }
);

export const fetchHandbooksForEmployee = createAsyncThunk(
  'handbook/fetchHandbooksForEmployee',
  async (employeeId, { rejectWithValue }) => {
    try {
      const { data } = await client.query({
        query: GET_HANDBOOKS_FOR_EMPLOYEE,
        variables: { employeeId },
        fetchPolicy: 'no-cache',
      });

      return data?.getHandbooksForEmployee || [];
    } catch (err) {
      return rejectWithValue(
        err?.message || err?.graphQLErrors?.[0]?.message || 'Failed to fetch handbooks for employee'
      );
    }
  }
);

export const upsertHandbook = createAsyncThunk(
  "handbook/upsertHandbook",
  async (
    {
      id,              // optional
      name,
      description,
      data,
      filename,
      mimetype,
      changedBy,
    },
    { rejectWithValue }
  ) => {
    try {
      const { data: res } = await client.mutate({
        mutation: UPSERT_HANDBOOK,
        variables: {
          id,
          name,
          description,
          data,
          filename,
          mimetype,
          changedBy,
        },
        fetchPolicy: "no-cache",
      });

      if (!res?.upsertHandbook) {
        return rejectWithValue("Failed to save handbook");
      }

      return res.upsertHandbook;
    } catch (err) {
      return rejectWithValue(
        err?.message ||
        err?.graphQLErrors?.[0]?.message ||
        "Failed to upsert handbook"
      );
    }
  }
);

export const { clearHandbookError } = handbookSlice.actions;
export default handbookSlice.reducer;